import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getAuth, hasCallCenterPermission } from 'actions/auth/login';
import { ERROR_ACCESS_DENIED } from 'lib/errorTypes';
import Raven from 'raven-js';
import { getLocale } from 'actions/language/selectors';
import { pushFormattedError } from 'helpers/formValidationHelper/formValidationMessage';
import * as Api from 'api/analytics';

// Actions
export const FETCH_DATA = 'CALL_CENTER/FETCH_DATA';
export const SUCCESS_FETCHING_DATA = 'CALL_CENTER/SUCCESS_FETCHING_DATA';
export const ERROR_FETCHING_DATA = 'CALL_CENTER/ERROR_FETCHING_DATA';
export const SET_IS_LOADING = 'CALL_CENTER/SET_IS_LOADING';

export const FETCH_DRAWER_DATA = 'CALL_CENTER/FETCH_DRAWER_DATA';
export const SET_DRAWER_DATA = 'CALL_CENTER/SET_DRAWER_DATA';
export const SET_IS_DRAWER_LOADING = 'CALL_CENTER/SET_IS_DRAWER_LOADING';

/*** Action Creators ***/
export const fetchData = () => ({
  type: FETCH_DATA
});

export const fetchDataSuccess = (data) => ({
  type: SUCCESS_FETCHING_DATA,
  data
});

export const fetchDataError = (error) => ({
  type: ERROR_FETCHING_DATA,
  error
});

export const setIsLoading = () => ({
  type: SET_IS_LOADING
});

export const fetchDrawerData = (directory) => ({
  type: FETCH_DRAWER_DATA,
  directory
});

export const setDrawerData = (drawerData) => ({
  type: SET_DRAWER_DATA,
  drawerData
});

export const setIsDrawerLoading = (isDrawerLoading) => ({
  type: SET_IS_DRAWER_LOADING,
  isDrawerLoading
});

/*** Reducer  ***/
export const DEFAULT_STATE = {
  isLoading: false,
  data: null,
  isDrawerLoading: true,
  drawerData: null
};

export default function CallCenterUtilizationReducer(
  state = DEFAULT_STATE,
  { data, error, type, isDrawerLoading, drawerData } = {}
) {
  switch (type) {
    case SUCCESS_FETCHING_DATA:
      return {
        ...state,
        isLoading: false,
        data
      };
    case ERROR_FETCHING_DATA:
      Raven.captureException(error, { state });
      return {
        ...state,
        isLoading: false,
        error
      };
    case SET_IS_LOADING:
      return {
        ...state,
        isLoading: true
      };
    case SET_DRAWER_DATA:
      return {
        ...state,
        drawerData
      };
    case SET_IS_DRAWER_LOADING:
      return {
        ...state,
        isDrawerLoading
      };
    default:
      return state;
  }
}

/*** Sagas ***/
// Worker
export function* reloadCallCenterData() {
  yield put(fetchData());
}

const ACCESS_DENIED_ERROR = 'Access denied error.';

export function* fetchCallCenterDataWorker() {
  try {
    const authorization = yield select(getAuth);
    const hasPermission = yield select(hasCallCenterPermission);

    if (!authorization || !hasPermission) {
      yield pushFormattedError({ id: ERROR_ACCESS_DENIED });
      return;
    }

    const locale = yield select(getLocale);
    yield put(setIsLoading());

    const data = yield call(Api.getCallCenterReports, authorization, locale);
    yield put(fetchDataSuccess(data));
  } catch (err) {
    if (
      err.errors &&
      err.errors.some((error) => error.message == ACCESS_DENIED_ERROR)
    ) {
      yield pushFormattedError({ id: ERROR_ACCESS_DENIED });
    } else {
      yield pushFormattedError(err);
    }
  }
}

export function* fetchCallCenterDrawerDataWorker({ directory }) {
  try {
    yield put(setIsDrawerLoading(true));
    const authorization = yield select(getAuth);
    const hasPermission = yield select(hasCallCenterPermission);

    if (!authorization || !hasPermission) {
      yield pushFormattedError({ id: ERROR_ACCESS_DENIED });
      return;
    }

    const locale = yield select(getLocale);
    const data = yield call(
      Api.getCallCenterReports,
      authorization,
      locale,
      directory
    );
    yield put(setDrawerData(data));
  } catch (err) {
    if (
      err.errors &&
      err.errors.some((error) => error.message == ACCESS_DENIED_ERROR)
    ) {
      yield pushFormattedError({ id: ERROR_ACCESS_DENIED });
    } else {
      Raven.captureException(new Error('Call Center Drawer Data Fetch Error'), {
        extra: { directory, err }
      });
      yield pushFormattedError(err);
    }
  } finally {
    yield put(setIsDrawerLoading(false));
  }
}

// Watcher
export function* fetchCallCenterUtilizationSaga() {
  yield all([
    takeLatest(FETCH_DATA, fetchCallCenterDataWorker),
    takeLatest(FETCH_DRAWER_DATA, fetchCallCenterDrawerDataWorker)
  ]);
}



// WEBPACK FOOTER //
// ./src/actions/callCenterUtilization/actions.js